import React, { useContext } from 'react';
import { InputText } from '@/components/form';
import Icons from '@/const/icons';
import { formatCurrencyAlt } from '@/utils';
import { PaymentMethodEnum, PaymentSummary, UserToPayItem } from '@/interfaces';
import {
	FieldValues,
	UseFormGetValues,
	UseFormRegister,
	UseFormSetValue,
} from 'react-hook-form';
import { Avatar, Button, Icon, Typography } from '@/components/common';
import SimpleTable from '@/components/common/tables/simpleTable';
import { CaseContext } from '@/context/CaseContext';
import { ClientFilterPayment } from './ClientFilterPayment';
import { StateFilterPayment } from './StateFilterPayment';
import {
	casesLawyerService,
	GET_SETTLEMENT_DETAILS,
	GET_SETTLEMENT_SUMMARY,
} from '@/api';
import { useDebouncedCallback } from 'use-debounce';
import clsx from 'clsx';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';

interface Props {
	users: UserToPayItem[] | undefined;
	groupAmount: number | undefined;
	inputError: { message: string } | null;
	paymentMethod: PaymentMethodEnum | undefined;
	setInputError: (value: { message: string } | null) => void;
	register: UseFormRegister<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	getValues: UseFormGetValues<FieldValues>;
	search: string;
	setSearch: (value: string) => void;
	handleSave: () => void;
	isDisabled?: boolean;
	paymentSummary: PaymentSummary | undefined;
	totalUsers: number;
	lastItemCallback: (node: HTMLDivElement | HTMLLIElement | null) => void;
	refetchUserData: () => void;
}

export const PayIndividually: React.FC<Props> = ({
	users,
	inputError,
	groupAmount,
	paymentMethod,
	setInputError,
	getValues,
	register,
	setValue,
	search,
	setSearch,
	handleSave,
	isDisabled = false,
	paymentSummary,
	totalUsers,
	lastItemCallback,
	refetchUserData,
}) => {
	const router = useRouter();
	const { id } = router.query;
	const queryClient = useQueryClient();
	const { isCaseReadOnly } = useContext(CaseContext);
	const disabled = isCaseReadOnly || isDisabled;

	React.useEffect(() => {
		if (!users || users?.length === 0) return;
		if (paymentMethod === PaymentMethodEnum.GROUP) {
			setValue('amount', formatCurrencyAlt(String(groupAmount ?? 0), 'blur'));
		}
	}, [users, paymentMethod, groupAmount]);

	React.useEffect(() => {
		users?.forEach((item) => {
			setValue(
				`settlement_amount.${item.application_id}`,
				formatCurrencyAlt(String(item.amount), 'blur') ?? 0
			);
		});
	}, [users]);

	const handleGroupBlur = () => {
		const amount = getValues('amount');
		const formattedAmount = formatCurrencyAlt(amount, 'blur');
		setValue('amount', formattedAmount);
	};

	const handleGroupChange = (event: any) => {
		const rawValue = event.target.value;
		const formattedAmount = formatCurrencyAlt(rawValue, handleGroupBlur);
		setValue('amount', formattedAmount);

		if (inputError?.message) {
			setInputError(null);
		}
	};

	const handleSaveSettlementAmount = async (
		application_id: number,
		amount: number
	) => {
		try {
			await casesLawyerService.confirmPaymentByUser(application_id, amount);
			// const formattedAmount = formatCurrencyAlt(String(amount), 'blur');
			// setValue(`settlement_amount.${application_id}`, formattedAmount);
			refetchUserData();
			queryClient.refetchQueries({
				queryKey: [GET_SETTLEMENT_DETAILS, id],
			});
			queryClient.refetchQueries({
				queryKey: [GET_SETTLEMENT_SUMMARY, id],
			});
		} catch (error) {
			console.log(error);
		}
	};

	const debouncedFunction = useDebouncedCallback(
		(application_id: number, amount: number) => {
			handleSaveSettlementAmount(application_id, amount);
		},
		1000
	);

	return (
		<div className="space-y-8">
			<div className="flex items-center gap-x-2 bg-bg-white p-5 rounded-2xl border border-grey-100">
				<Typography type="regular-16">Apply amount to all:</Typography>
				<InputText
					disabled={disabled}
					register={register}
					name="amount"
					className="w-[170px]"
					iconLeft={disabled ? Icons.currency : Icons.currencyBlack}
					onChange={(e) => handleGroupChange(e)}
					onBlur={() => handleGroupBlur()}
					classNameIcon="!h-4 !w-4 !text-primary !top-[30%]"
					classNameInput="!pl-9 !mt-0 h-8"
				/>
				<Button
					label="Apply to All"
					decoration="primary"
					className="h-8"
					size="fit"
					disabled={disabled}
					onClick={handleSave}
				/>
			</div>
			<div>
				<div className="flex items-center justify-between w-full py-2 px-4 border border-grey-100 rounded-t-2xl bg-gray-15">
					<Typography type="regular-14" className="text-black">
						Settlement Payment
					</Typography>
					<Typography type="regular-14" className="text-opacity-black">
						{totalUsers ?? 0}/{paymentSummary?.paid_count ?? 0} paid
					</Typography>
				</div>
				<SimpleTable<
					UserToPayItem & {
						isActive: boolean;
					}
				>
					hideRowActions
					classNameContainer="!rounded-t-none !rounded-b-2xl shadow-none !pr-0 max-h-[400px] overflow-y-auto"
					classNameHeader="grid grid-cols-[1.5fr,1fr,1.5fr,1fr] !bg-gray-15"
					classNameRows="grid grid-cols-[1.5fr,1fr,1.5fr,1fr] hover:!bg-transparent hover:!border-b-dark-10 hover:!border-t-dark-10 hover:!border-x-transparent !rounded-none"
					classNameHead="!opacity-100"
					classNameHeaderItem="!px-4 !py-0 my-auto"
					lastItemRef={lastItemCallback}
					columns={[
						{
							header: (
								<ClientFilterPayment setSearch={setSearch} search={search} />
							),
							content: (instance) => (
								<div className="flex items-center gap-x-2">
									<Avatar
										photoUrl={instance.user_profile_picture ?? ''}
										firstname={instance.user_firstname}
										lastname={instance.user_lastname}
										size="extra-small"
									/>
									<span className="text-xs text-black">
										{instance.user_firstname} {instance.user_lastname}
									</span>
								</div>
							),
						},
						{
							header: <StateFilterPayment />,
							content: (instance) => (
								<span className="text-xs text-black capitalize">
									{instance.state_name}
								</span>
							),
						},
						{
							header: 'Settlement Amount',
							content: (instance) => (
								<InputText
									disabled={instance.payment_status === 'SUCCESS' || disabled}
									register={register}
									name={`settlement_amount.${instance.application_id}`}
									className="w-full"
									iconLeft={
										instance.payment_status === 'SUCCESS' || disabled
											? Icons.currency
											: Icons.currencyBlack
									}
									onChangeCustom={(e: any) => {
										debouncedFunction(
											instance.application_id,
											Number(e.target.value)
										);
									}}
									classNameIcon="!h-4 !w-4 !text-primary !top-[30%]"
									classNameInput="!pl-9 !mt-0 h-8"
								/>
							),
						},
						{
							header: 'Status',
							content: (instance) => (
								<div
									className={clsx(
										'flex items-center gap-x-1 text-xs text-black px-6 py-2 rounded-full font-semibold w-[100px]',
										{
											'bg-[#2084E71A]': instance.payment_status === 'PENDING',
											'border border-green-base':
												instance.payment_status === 'SUCCESS',
											'border border-main-red':
												instance.payment_status === 'FAILED',
										}
									)}
								>
									{instance.payment_status === 'SUCCESS' && (
										<Icon src={Icons.check} width={16} height={16} />
									)}
									{instance.payment_status === 'PENDING'
										? 'Pending'
										: instance.payment_status === 'FAILED'
										? 'Failed'
										: instance.payment_status === 'SUCCESS'
										? 'Paid'
										: ''}
								</div>
							),
						},
					]}
					rows={users?.map((item, index) => ({
						...item,
						isActive: false,
						index,
					}))}
					hasPagination={false}
				/>
			</div>
			{/* <Input
				iconRight={Icons.infoCircle}
				iconLeft={Icons.currencyBlack}
				type="text"
				name="amount"
				register={register}
				className="w-full"
				classNameInput={'h-[1.875rem] pl-9'}
				classNameIcon="!top-[40%] !size-4"
				pattern="^\$\d{1,3}(,\d{3})*(\.\d+)?$"
				onChange={handleGroupChange}
				onBlur={() => handleGroupBlur()}
				tooltipLabel="The amount you entered is the amount that will be paid to each individual customer. Make sure it is the correct amount."
				error={inputError}
			/> */}
		</div>
	);
};
